import React, { useEffect } from "react"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import { withRouter } from "react-router-dom"
import queryString from "query-string"
import Helmet from "react-helmet"
import { updateAccount, signUp } from "@api/account"
import { getSubscriptionPrice } from "@api/prices"
import { getUserLocation } from "@api/utilities"
import GoogleIcon from "@src/media/icons/google_icon.png"
import { GLOBALS } from "@src/config"
import Button from "../../../components/button"
import SignUpForm from "./signup_form"
import CreditCardForm from '@src/pages/settings/pages/billing/components/add/form'
import Address from "./address"

const perks = [
  '1TB of storage',
  '20 monthly studio-grade DRM-encoded screeners',
  'Screener rooms and analytics',
  'Aspera file requests',
  '... and so much more!'
]

const SignUp = ({ account, updateAccount, signUp, price, card, submitting, error, getSubscriptionPrice, getUserLocation }) => {
  const callbackQueries = `?${queryString.stringify(queryString.parse(window.location.search))}`
  const festival = queryString.parse(window.location.search).festival || null

  useEffect(() => {
    getUserLocation()
    if (festival) {
      updateAccount({ festival })
    }
  }, [festival, updateAccount, getUserLocation])

  useEffect(() => {
    if (!festival) {
      getSubscriptionPrice({ countryCode: account.country_code || 'US' })
    }
  }, [account.country_code, festival, getSubscriptionPrice])

  return (
    <>
      <Helmet title='CineSend | Sign Up'/>
      <form>
        {!festival && price && price.interval_amount_formatted && (
          <>
            <h4 className={'center border-gray-5 border-bottom mb2 pb2'}>
              Sign up today for {price.interval_amount_formatted}
            </h4>
            <div className={'fs12 bold italic mb1'}>Your subscription includes:</div>
            {perks.map((perk, i) => <li className='fs12' key={i}>{perk}</li>)}
            <div className={'border-gray-5 border-top mt2 mb3'}/>
          </>
        )}
        <SignUpForm
          festival={festival}
          showButton={!!festival}
          hideCountry={!festival}
          buttonText='Sign up'
          onButtonClick={event => {
            event.preventDefault()
            event.stopPropagation()
            signUp()
          }}
          disabled={account.status === "PENDING" || !account.full_name || !account.email || !account.password || !account.country_code}
          buttonStatus={account.status.toLowerCase()}
        />
        {!festival && (
          <>
            <div className={'border-gray-5 border-top mt3 mb2'}/>
            <h5 className={'mb2'}>Billing Details</h5>
            <CreditCardForm newPaymentMethod={'credit-card'}/>
            <Address/>
            {error && <div className={'my1 red bold'}>{error}</div>}
            <div className={'fs12 muted my1'}>By signing up you agree to the <a rel="noopener noreferrer" className="bold underline" href='https://cinesend.com/terms-and-conditions' target='_blank'>Terms and Conditions.</a></div>
            <button
              id='cinesend-final-sign-up-button'
              type='submit'
              tabIndex='1'
              data-test-id='account-submit-btn'
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                signUp(null)
              }}
              disabled={submitting
                || !account.full_name || !account.email || !account.password || !account.country_code
                || !account.address_line1
                || !account.postal_code
                || !account.mobile_number
                || account.status === "PENDING"
                || [
                  "credit_card_cardholder_name",
                  "credit_card_card_number",
                  "credit_card_cvc",
                  "credit_card_expiry"
                ].filter(key => !card[key]).length > 0}
              className={`cs-button ${account.status.toLowerCase()} col-12 cinesend-final-sign-up-button`}>
              {price && price.interval_amount_formatted ? `Sign up for ${price.interval_amount_formatted}` : 'Sign up'}
            </button>
          </>
        )}
      </form>
      <div className='mt3 py1 flex justify-center'>
        Or, sign up with your Google Account:
      </div>
      <a href={GLOBALS.SOCIAL_AUTH_URL + callbackQueries}>
        <Button
          id='cinesend-sign-up-with-google-button'
          className='cs-button white col-12 flex items-center justify-center p0 cinesend-sign-up-with-google-button'
          tabIndex='2'
          style={{ background: 'white' }}
          onClick={e => {
            e.preventDefault()
            window.location = GLOBALS.SOCIAL_AUTH_URL + callbackQueries
          }}>
          <div className='inline-flex items-center'>
            <img
              className='cs-img'
              src={GoogleIcon}
              alt='Sign in with Google'
              style={{
                width: '28px',
                height: '28px'
              }}/>
            <div className='pl1 flex items-center'>
              Google
            </div>
          </div>
        </Button>
      </a>
    </>
  )
}

const mapStateToProps = state => ({
  account: state.account,
  card: state.settings.billing.new,
  fetchingPrice: state.prices.subscription.status === 'PENDING',
  price: state.prices.subscription.price.monthly,
  submitting: state.account.status === 'PENDING',
  error: state.account.error_message
})

const mapDispatchToProps = dispatch => ({
  updateAccount: bindActionCreators(updateAccount, dispatch),
  signUp: bindActionCreators(signUp, dispatch),
  getSubscriptionPrice: bindActionCreators(getSubscriptionPrice, dispatch),
  getUserLocation: bindActionCreators(getUserLocation, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SignUp))
